import { Reveal } from "@/lib/motion";

// Small mono eyebrow label used above section headings.
export function Eyebrow({ children, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-indigo-500/60" />
      <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-indigo-300">
        {children}
      </span>
    </div>
  );
}

// Section wrapper with consistent id, spacing and container.
export function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`relative py-24 sm:py-28 lg:py-32 ${className}`}>
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">{children}</div>
    </section>
  );
}

// Standard section heading block (eyebrow + title + optional lead).
export function SectionHeading({ eyebrow, title, lead, align = "left", className = "" }) {
  const alignCls = align === "center" ? "items-center text-center mx-auto" : "items-start text-left";
  return (
    <Reveal className={`flex max-w-3xl flex-col gap-5 ${alignCls} ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
        {title}
      </h2>
      {lead && <p className="text-base leading-relaxed text-gray-400 sm:text-lg">{lead}</p>}
    </Reveal>
  );
}

// Small pill badge (e.g. PLANNED / FUTURE).
export function Tag({ children, tone = "planned", className = "", ...rest }) {
  const tones = {
    planned: "border-indigo-400/30 bg-indigo-500/10 text-indigo-200",
    future: "border-violet-400/30 bg-violet-500/10 text-violet-200",
    neutral: "border-white/15 bg-white/5 text-gray-300",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${tones[tone] || tones.neutral} ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

// Glass card container with hover glow.
export function GlassCard({ children, className = "", ...rest }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl transition-all duration-500 hover:border-white/20 hover:bg-white/[0.05] ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
